<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

// Identificação da conta e da versão instalada, no fim das configurações.
const { t } = useI18n();
const { currentAccount } = useAccount();
const globalConfig = useMapGetter('globalConfig/get');

const accountId = computed(() => currentAccount.value?.id?.toString() || '');
// Versão da Talkvia (número e nome); a base Chatwoot aparece somente no Super Admin.
const talkviaVersion = computed(() => globalConfig.value?.talkviaVersion || '');
const talkviaVersionName = computed(
  () => globalConfig.value?.talkviaVersionName || ''
);
const gitSha = computed(() => (globalConfig.value?.gitSha || '').slice(0, 7));

const copy = async value => {
  try {
    await copyTextToClipboard(value);
    useAlert(t('GENERAL_SETTINGS.FOOTER.COPIED'));
  } catch {
    useAlert(t('GENERAL_SETTINGS.FOOTER.COPY_ERROR'));
  }
};

const items = computed(() => [
  {
    key: 'account',
    icon: 'i-lucide-hash',
    label: t('GENERAL_SETTINGS.FOOTER.ACCOUNT_ID'),
    value: accountId.value,
    hint: t('GENERAL_SETTINGS.FOOTER.ACCOUNT_ID_HINT'),
    copyValue: accountId.value,
  },
  {
    key: 'version',
    icon: 'i-lucide-package',
    label: t('GENERAL_SETTINGS.FOOTER.VERSION'),
    value: talkviaVersion.value ? `v${talkviaVersion.value}` : '—',
    hint: [
      talkviaVersionName.value &&
        t('GENERAL_SETTINGS.FOOTER.VERSION_NAME', {
          name: talkviaVersionName.value,
        }),
      gitSha.value && t('GENERAL_SETTINGS.FOOTER.BUILD', { sha: gitSha.value }),
    ]
      .filter(Boolean)
      .join(' · '),
    copyValue: globalConfig.value?.gitSha || '',
  },
]);
</script>

<template>
  <footer
    class="mt-8 rounded-xl border border-n-weak bg-n-solid-2 p-4"
    :aria-label="t('GENERAL_SETTINGS.FOOTER.TITLE')"
  >
    <div class="grid gap-3 sm:grid-cols-2">
      <div
        v-for="item in items"
        :key="item.key"
        class="flex min-w-0 items-center gap-3 rounded-lg bg-n-alpha-1 px-3 py-2.5"
      >
        <span
          class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11"
        >
          <span :class="item.icon" class="size-4" />
        </span>
        <div class="min-w-0 flex-1">
          <p class="mb-0 text-xs text-n-slate-11">{{ item.label }}</p>
          <p
            class="mb-0 truncate font-mono text-sm font-medium text-n-slate-12"
          >
            {{ item.value }}
          </p>
          <p v-if="item.hint" class="mb-0 truncate text-xs text-n-slate-10">
            {{ item.hint }}
          </p>
        </div>
        <button
          v-if="item.copyValue"
          type="button"
          class="grid size-8 shrink-0 place-items-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          :title="t('GENERAL_SETTINGS.FOOTER.COPY', { label: item.label })"
          :aria-label="t('GENERAL_SETTINGS.FOOTER.COPY', { label: item.label })"
          @click="copy(item.copyValue)"
        >
          <span class="i-lucide-copy size-4" />
        </button>
      </div>
    </div>
  </footer>
</template>
